import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ScanSearch, ShieldAlert, FileCheck2, Workflow, Boxes, Plug, ArrowUpRight, ArrowRight } from "lucide-react";
import { useContentLibrary } from "@/data/content";

const icons = [ScanSearch, Boxes, ShieldAlert, Workflow, FileCheck2, Plug];
const isFlagshipProduct = (product) => product.flagship;

export const Product = () => {
  const { data } = useContentLibrary();
  const products = data.products;
  const flagship = products.find(isFlagshipProduct) || products[0];

  if (!flagship) return null;

  const capabilities = flagship.capabilities.slice(0, 5);
  const featuredCapability = capabilities[0];
  const supportingCapabilities = capabilities.slice(1);
  const otherProducts = products.filter((p) => p.slug !== flagship.slug);

  return (
    <section id="product" className="relative py-24 md:py-32 bg-white dark:bg-zinc-950">
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
        <div className="grid grid-cols-12 gap-8 items-end mb-12">
          <div className="col-span-12 lg:col-span-8">
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#002FA7] dark:text-[#7C9BFF]">
              Product Portfolio
            </span>
            <h2 className="mt-4 font-display font-black tracking-tighter text-zinc-950 dark:text-zinc-50 text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
              More to explore.
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-4">
            <Link
              to="/products"
              data-testid="products-index-link"
              className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#002FA7] dark:text-[#7C9BFF] hover:text-[#00227A] dark:hover:text-white"
            >
              View all products <ArrowRight size={15} />
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <motion.div
            data-testid="flagship-product-card"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.55 }}
            className="group relative lg:col-span-7 border border-zinc-200 bg-white p-8 md:p-10 rounded-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_-20px_rgba(9,9,11,0.18)] hover:border-zinc-950 dark:bg-zinc-900 dark:border-zinc-800 dark:hover:border-zinc-50 dark:hover:shadow-[0_20px_50px_-20px_rgba(124,155,255,0.25)]"
          >
            <div className="flex items-start justify-between gap-5">
              <div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-[#002FA7] dark:text-[#7C9BFF]">
                  {flagship.eyebrow || "Flagship Product"}
                </div>
                <h3 className="mt-5 font-display font-black tracking-tighter text-3xl sm:text-4xl text-zinc-950 dark:text-zinc-50 leading-[1]">
                  {flagship.title}
                </h3>
              </div>
              <ArrowUpRight
                size={18}
                className="text-zinc-400 dark:text-zinc-500 opacity-0 group-hover:opacity-100 group-hover:text-[#002FA7] dark:group-hover:text-[#7C9BFF] transition-all shrink-0"
              />
            </div>
            <p className="mt-5 text-zinc-600 dark:text-zinc-400 leading-relaxed max-w-2xl">
              {flagship.description}
            </p>

            {flagship.heroStats.length > 0 && (
              <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-0 border-y border-zinc-200 dark:border-zinc-800 divide-x divide-zinc-200 dark:divide-zinc-800">
                {flagship.heroStats.map((s) => (
                  <Stat key={s.v} k={s.k} v={s.v} />
                ))}
              </div>
            )}

            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to={`/products/${flagship.slug}`}
                data-testid="product-deepdive-link"
                className="inline-flex items-center gap-2 bg-[#002FA7] hover:bg-[#00227A] text-white px-5 py-3 rounded-sm text-sm font-semibold transition-colors"
              >
                Explore the flagship <ArrowRight size={15} />
              </Link>
              <Link
                to="/products"
                className="inline-flex items-center gap-2 border border-zinc-950 text-zinc-950 hover:bg-zinc-950 hover:text-white dark:border-zinc-200 dark:text-zinc-50 dark:hover:bg-zinc-50 dark:hover:text-zinc-950 px-5 py-3 rounded-sm text-sm font-semibold transition-colors"
              >
                Browse products
              </Link>
            </div>
          </motion.div>

          <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-6">
            {featuredCapability && <CapabilityCard capability={featuredCapability} index={0} featured />}
            {supportingCapabilities.slice(0, 2).map((capability, index) => (
              <CapabilityCard key={capability.title} capability={capability} index={index + 1} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const CapabilityCard = ({ capability, index, featured = false }) => {
  const Icon = icons[index % icons.length];

  return (
    <motion.div
      data-testid={`landing-product-capability-${index}`}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.55, delay: index * 0.06 }}
      className={`border border-zinc-200 bg-white p-6 rounded-sm dark:bg-zinc-900 dark:border-zinc-800 ${featured ? "sm:col-span-2 lg:col-span-1" : ""}`}
    >
      <Icon size={featured ? 28 : 22} strokeWidth={1.5} className="text-[#002FA7] dark:text-[#7C9BFF]" />
      <h4 className={`mt-5 font-display font-bold tracking-tight text-zinc-950 dark:text-zinc-50 ${featured ? "text-2xl" : "text-xl"}`}>
        {capability.title}
      </h4>
      <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">
        {capability.desc}
      </p>
    </motion.div>
  );
};

const Stat = ({ k, v }) => (
  <div className="px-4 py-5 first:pl-0">
    <div className="font-display font-black tracking-tighter text-2xl sm:text-3xl text-zinc-950 dark:text-zinc-50">{k}</div>
    <div className="mt-1 text-[11px] font-mono uppercase tracking-widest text-zinc-500 dark:text-zinc-400">{v}</div>
  </div>
);

export default Product;